import React, { useEffect, useState } from 'react'
import { Form, Input, message, Upload, Button } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { FaCamera, FaTrashAlt } from 'react-icons/fa'
import { translations } from '../../utils/constants'
import { secureApi } from '../../Config/api'
import { POST } from '../../utils/apis'
import { loginUser } from '../../Redux/actions/authActions'
import { ROLE_LABELS, userInitials } from './Settings'

const MAX_IMAGE_BYTES = 2 * 1024 * 1024
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp']

// Personal information + profile photo. Saves through the self-service
// update-profile endpoint (the server takes the user id from the session, so
// this can never edit anyone else) and refreshes the logged-in user in redux
// so the sidebar and header pick the change up at once.
const Profile = ({ language }) => {
  const dispatch = useDispatch()
  const authUser = useSelector((state) => state?.authReducer?.user)
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)
  // Pending photo: a File to upload, null to remove, undefined to keep.
  const [photoFile, setPhotoFile] = useState(undefined)
  const [preview, setPreview] = useState(authUser?.profileImage || null)
  const t = translations[language] || {}

  useEffect(() => {
    form.setFieldsValue({
      username: authUser?.username || '',
      email: authUser?.email || '',
      phonenumber: authUser?.phonenumber || '',
      organization: authUser?.organization || '',
      role: ROLE_LABELS[authUser?.role] || authUser?.role || '',
    })
    setPreview(authUser?.profileImage || null)
    setPhotoFile(undefined)
  }, [authUser]) // eslint-disable-line

  // Release object URLs created for local previews.
  useEffect(() => () => {
    if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview)
  }, [preview])

  const beforeUpload = (file) => {
    if (!IMAGE_TYPES.includes(file.type) || file.size > MAX_IMAGE_BYTES) {
      message.error(t.invalidImage)
      return Upload.LIST_IGNORE
    }
    setPhotoFile(file)
    setPreview(URL.createObjectURL(file))
    return false
  }

  const removePhoto = () => {
    setPhotoFile(null)
    setPreview(null)
  }

  const isDirty = () => {
    const v = form.getFieldsValue()
    return (
      photoFile !== undefined ||
      (v.username || '') !== (authUser?.username || '') ||
      String(v.phonenumber || '') !== String(authUser?.phonenumber || '')
    )
  }

  const onFinish = async (values) => {
    setSaving(true)
    try {
      let profileImage
      if (photoFile instanceof File) {
        const formData = new FormData()
        formData.append('files', photoFile)
        const uploadRes = await secureApi?.post(POST?.UPLOAD_FILE, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        profileImage = uploadRes?.fileUrls?.[0] || null
        if (!profileImage) {
          message.error(t.profileUpdateFailed)
          return
        }
      } else if (photoFile === null) {
        profileImage = null
      }

      const payload = {
        userName: values?.username?.trim(),
        phoneNumber: values?.phonenumber ? String(values.phonenumber).trim() : null,
        ...(profileImage !== undefined ? { profileImage } : {}),
      }
      const response = await secureApi?.post(POST?.UPDATE_PROFILE, payload)
      if (response?.success) {
        dispatch(loginUser({ ...authUser, ...(response?.user || {}) }))
        setPhotoFile(undefined)
        message.success(t.profileUpdated)
      } else {
        message.error(response?.error || response?.message || t.profileUpdateFailed)
      }
    } catch (error) {
      message.error(t.profileUpdateFailed)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='ps-section'>
      <div className='ps-section-head'>
        <h3>{t.personalInformation}</h3>
        <p>{t.personalInformationHint}</p>
      </div>

      <div className='ps-photo-row'>
        <div className='ps-photo'>
          {preview ? (
            <img src={preview} alt='profile' />
          ) : (
            <span>{userInitials(authUser?.username, authUser?.email)}</span>
          )}
        </div>
        <div className='ps-photo-actions'>
          <span className='ps-field-label'>{t.profilePhoto}</span>
          <div className='ps-photo-buttons'>
            <Upload accept='image/png,image/jpeg,image/webp' showUploadList={false} beforeUpload={beforeUpload}>
              <Button className='ps-btn-secondary' icon={<FaCamera />}>{t.uploadPhoto}</Button>
            </Upload>
            {preview && (
              <Button className='ps-btn-ghost' icon={<FaTrashAlt />} onClick={removePhoto}>
                {t.removePhoto}
              </Button>
            )}
          </div>
          <span className='ps-hint'>{t.photoHint}</span>
        </div>
      </div>

      <Form form={form} layout='vertical' onFinish={onFinish} requiredMark={false} className='ps-form'>
        <div className='ps-form-grid'>
          <Form.Item
            label={t.fullName}
            name='username'
            rules={[{ required: true, whitespace: true, message: t.pleaseEnterName }]}
          >
            <Input className='custom-inputs' placeholder={t.fullName} maxLength={80} />
          </Form.Item>
          <Form.Item label={t.emailAddress} name='email' extra={t.emailLocked}>
            <Input className='custom-inputs' placeholder={t.emailAddress} disabled />
          </Form.Item>
          <Form.Item
            label={t.phoneNumber}
            name='phonenumber'
            normalize={(value) => value.replace(/[^0-9+ ]/g, '').replace(/(?!^)\+/g, '')}
            rules={[{ pattern: /^\+?[0-9 ]{5,20}$/, message: t.invalidPhone }]}
          >
            <Input className='custom-inputs' type='tel' inputMode='tel' autoComplete='tel' placeholder='+39 ...' />
          </Form.Item>
          <Form.Item label={t.organizationLabel} name='organization'>
            <Input className='custom-inputs capitalized-name' placeholder={t.organizationLabel} disabled />
          </Form.Item>
          <Form.Item label={t.roleLabel} name='role'>
            <Input className='custom-inputs' placeholder={t.roleLabel} disabled />
          </Form.Item>
        </div>

        <div className='ps-form-actions'>
          <Form.Item shouldUpdate noStyle>
            {() => (
              <Button type='primary' htmlType='submit' className='add-button ps-save' loading={saving} disabled={!isDirty()}>
                {t.saveChanges}
              </Button>
            )}
          </Form.Item>
        </div>
      </Form>
    </div>
  )
}

export default Profile
